// Sites API services
import { apiClient, type ApiResponse } from '@/services/api';
import type {
  Site,
  SiteCreation,
  SiteChanges,
  Material,
  SiteMaterial,
  Photo,
  NewPhotoData,
  NewPhotosData,
  ChangedSiteMaterials,
  MaterialListFormat,
} from '../types';
import type { ApiResult, ApiError } from '@/types';

export const sitesApi = {
  // Get all sites
  getSites: (projectId: number): Promise<ApiResult<{ data: Site[] }>> => {
    return apiClient.get<{ data: Site[] }, undefined>(
      `/api/projects/${projectId}/sites`
    );
  },

  // Get single site by ID
  getSite: (siteId: number): Promise<ApiResult<{ data: Site }>> => {
    return apiClient.get<{ data: Site }, undefined>(`/api/sites/${siteId}`);
  },

  // Create new site
  createSite: (siteData: SiteCreation): Promise<ApiResult<unknown>> => {
    return apiClient.post<SiteCreation, unknown>('/api/sites', siteData);
  },

  deleteSite: (siteId: number): Promise<ApiResult<unknown>> => {
    return apiClient.delete<unknown, undefined>(`/api/sites/${siteId}`);
  },

  // Update site
  updateSite: (
    siteId: number,
    changes: SiteChanges
  ): Promise<ApiResult<{ data: Site }>> => {
    return apiClient.put<{ id: number } & SiteChanges, { data: Site }>(
      `/api/sites/${siteId}`,
      {
        id: siteId,
        ...changes,
      }
    );
  },

  // Get materials list
  getMaterialsList: (): Promise<ApiResult<{ data: Material[] }>> => {
    return apiClient.get<{ data: Material[] }, undefined>('/api/materials');
  },

  // Get site materials (legacy method)
  getSiteMaterials: (
    siteId?: number
  ): Promise<ApiResult<{ data: SiteMaterial[] }>> => {
    const url = siteId ? `/api/sites/${siteId}/materials` : '/api/materials';
    return apiClient.get<{ data: SiteMaterial[] }, { siteId?: number }>(
      url,
      siteId ? { siteId } : undefined
    );
  },

  // Delete multiple site materials by IDs
  deleteSiteMaterials: (
    siteId: number,
    materialIds: number[]
  ): Promise<ApiResult<unknown>> => {
    return apiClient.deleteBody<unknown, unknown>(
      `/api/sites/${siteId}/materials`,
      {
        siteId,
        materialIds,
      }
    );
  },

  // Update multiple site materials (using bulk update API)
  updateSiteMaterials: async (
    materials: ChangedSiteMaterials[]
  ): Promise<ApiResult<unknown>> => {
    if (materials.length === 0) {
      return {
        isSuccess: true,
        result: undefined,
        error: undefined,
      };
    }

    // Group materials by siteId
    const materialsBySite = materials.reduce(
      (acc, material) => {
        const siteId = material.siteId;
        if (!acc[siteId]) {
          acc[siteId] = [];
        }
        acc[siteId].push({
          materialId: material.materialId,
          quantity: material.quantity,
        });
        return acc;
      },
      {} as Record<number, Array<{ materialId: number; quantity: number }>>
    );

    try {
      // Process each site's materials
      const results = await Promise.all(
        Object.entries(materialsBySite).map(([siteId, siteMaterials]) =>
          apiClient.put<
            {
              siteId: number;
              materials: Array<{ materialId: number; quantity: number }>;
            },
            unknown
          >(`/api/sites/${siteId}/materials`, {
            siteId: Number(siteId),
            materials: siteMaterials,
          })
        )
      );

      // Return success if all updates succeeded
      return {
        isSuccess: true,
        result: results,
        error: undefined,
      };
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : String(error);
      const apiError: ApiError = {
        message: errorMessage,
        status: undefined,
        code: undefined,
      };

      return {
        isSuccess: false,
        result: undefined,
        error: apiError,
      };
    }
  },

  // Get site photos
  getSitePhotos: (siteId: number): Promise<ApiResult<{ data: Photo[] }>> => {
    return apiClient.get<{ data: Photo[] }, undefined>(
      `/api/sites/${siteId}/photos`
    );
  },

  // Get compressed photos for multiple sites
  getCompressedPhotos: (
    siteId: number
  ): Promise<ApiResult<{ data: Photo[] }>> => {
    return apiClient.get<{ data: Photo[] }, { compressed: boolean }>(
      `/api/sites/${siteId}/photos`,
      { compressed: true }
    );
  },

  // Add photo to site
  addPhoto: (
    photoData: NewPhotoData
  ): Promise<ApiResult<{ data: { base64Image: string; id: number } }>> => {
    // Remove data URL prefix if present (data:image/jpeg;base64, or data:image/png;base64,)
    const cleanBase64 = photoData.photoData.replace(
      /^data:image\/[a-zA-Z]+;base64,/,
      ''
    );

    return apiClient.post<
      {
        siteId: number;
        base64Image: string;
      },
      { data: { base64Image: string; id: number } }
    >(`/api/sites/${photoData.siteId}/photos`, {
      siteId: photoData.siteId,
      base64Image: cleanBase64,
    });
  },

  // Add multiple photos to site
  addPhotos: (
    photosData: NewPhotosData
  ): Promise<ApiResult<ApiResponse<unknown>>> => {
    return apiClient.post<NewPhotosData, ApiResponse<unknown>>(
      '/api/iss/photos',
      photosData
    );
  },

  // Delete photo
  deletePhoto: (
    photoId: number,
    siteId: number
  ): Promise<ApiResult<unknown>> => {
    return apiClient.delete<unknown, undefined>(
      `/api/sites/${siteId}/photos/${photoId}`
    );
  },

  // Rotate photo by 90 degrees clockwise
  rotatePhoto: (
    siteId: number,
    photoId: number
  ): Promise<ApiResult<{ data: { base64Image: string; id: number } }>> => {
    return apiClient.patch<
      undefined,
      { data: { base64Image: string; id: number } }
    >(`/api/sites/${siteId}/photos/${photoId}/rotate`, undefined);
  },

  // Get material list formats
  getMaterialListFormats: (): Promise<
    ApiResult<ApiResponse<MaterialListFormat[]>>
  > => {
    return apiClient.get<ApiResponse<MaterialListFormat[]>, undefined>(
      '/api/material-formats'
    );
  },

  // Get project material list formats
  getProjectMaterialListFormats: (
    projectId?: number
  ): Promise<ApiResult<ApiResponse<unknown[]>>> => {
    const url = projectId
      ? `/api/iss/projectMaterialListFormats?projectId=${projectId}`
      : '/api/iss/projectMaterialListFormats';
    return apiClient.get<ApiResponse<unknown[]>, { projectId?: number }>(
      url,
      projectId ? { projectId } : undefined
    );
  },
};

export * from '../types';
